"use client";
import {
  ADDRESS_FORM_FIELDS,
  INITIAL_ADDRESS_FORM_VALUES,
} from "@repo/constants/account";
import { COUNTRY_CODE_PHONE_PREFIX } from "@repo/constants/common";
import { useFormValidation } from "@repo/lib/hooks/common";
import { useTrackAddressEvent } from "@repo/lib/hooks/webengage";
import { normalizeSaudiPhone, validation } from "@repo/lib/utils";
import { addAddress, updateAddress } from "@repo/server-actions/account";
import {
  Button,
  Checkbox,
  ErrorMessage,
  Input,
  LoadingDots,
  SelectCity,
} from "@repo/ui";
import { useTranslations } from "next-intl";
import { useEffect, useState, useTransition } from "react";

const AddressForm = ({ session, formData, isEdit, handleAddressDrawer }) => {
  const t = useTranslations("Common");
  const [isPending, startTransition] = useTransition();
  const [isCheckboxLocked, setIsCheckboxLocked] = useState(false);
  const [isDefaultAddressChecked, setIsDefaultAddressChecked] = useState(
    formData?.defaultAddress || false
  );
  const { trackAddressEvent } = useTrackAddressEvent();

  const {
    formValues,
    formErrors,
    setFormErrors,
    handleValidation,
    setFormValues,
  } = useFormValidation(formData);

  useEffect(() => {
    if (formData) {
      const cleanedFormData = {
        ...formData,
        // Normalize phone number by removing +966, 966, 00, or leading 0
        phoneNumber: normalizeSaudiPhone(formData.phoneNumber),
      };
      setFormValues(cleanedFormData);
      setIsCheckboxLocked(cleanedFormData?.defaultAddress || false);
      setIsDefaultAddressChecked(cleanedFormData?.defaultAddress || false);
    }
  }, [formData, setFormValues]);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setIsDefaultAddressChecked(checked);
    handleValidation(e);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = validation(formValues, t);
    if (Object.keys(errors).length === 0) {
      const payload = { ...formValues, country: "SA" };
      // Normalize phone number by removing +966, 966, 00, or leading 0
      if (payload.phoneNumber) {
        payload.phoneNumber = normalizeSaudiPhone(payload.phoneNumber);
      }
      startTransition(async () => {
        let response;
        if (isEdit) {
          response = await updateAddress(payload, session);
        } else {
          response = await addAddress(payload, session);
        }
        if (response?.success) {
          trackAddressEvent(response, isEdit);
          setIsCheckboxLocked(payload?.defaultAddress || false);
          handleAddressDrawer();
          setFormValues(INITIAL_ADDRESS_FORM_VALUES);
        }
      });
    } else {
      setFormErrors(errors);
      console.log("Failed to submit the form, please check the form fields");
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-wrap p-4">
        {ADDRESS_FORM_FIELDS?.map(
          ({ id, type, name, placeholder, label, readOnly, disabled }) => (
            <div key={id} className="mb-4 w-full last:mb-0">
              {type === "select" ? (
                <SelectCity
                  label={t("City")}
                  defaultValue={t("Select_City")}
                  name={name}
                  value={formValues?.[name]}
                  disabled={disabled}
                  onChange={handleValidation}
                />
              ) : type === "checkbox" ? (
                <Checkbox
                  type="checkbox"
                  name={name}
                  value={formValues?.[name]}
                  onChange={handleCheckboxChange}
                  readOnly={readOnly || isCheckboxLocked}
                  disabled={disabled || isCheckboxLocked}
                  label={t(label)}
                  checked={isDefaultAddressChecked}
                />
              ) : name === "phoneNumber" ? (
                <div>
                  <label className="mb-2 block text-sm font-medium text-[#5E5E5E]">
                    {t(label)}
                  </label>
                  <div className="relative">
                    {
                      <span
                        className="text-supportiveBlack absolute mt-0.5 inline-flex h-8 items-center bg-gray-100 px-2 text-base font-medium md:h-10 ltr:ml-0.5 rtl:mr-0.5"
                        dir="ltr"
                      >
                        {COUNTRY_CODE_PHONE_PREFIX}
                      </span>
                    }
                    <Input
                      type="tel"
                      name="phoneNumber"
                      placeholder={t(placeholder)}
                      value={`${formValues?.phoneNumber}`}
                      onChange={handleValidation}
                      className="ltr:pl-16 rtl:pr-14 rtl:text-right"
                    />
                  </div>
                  <ErrorMessage message={formErrors?.[name]} />
                </div>
              ) : (
                <>
                  <Input
                    type="text"
                    name={name}
                    placeholder={t(placeholder)}
                    value={formValues?.[name]}
                    onChange={handleValidation}
                    readOnly={readOnly}
                    disabled={disabled}
                    label={t(label)}
                  />
                  <ErrorMessage message={formErrors?.[name]} />
                </>
              )}
            </div>
          )
        )}
      </div>
      <div className="px-4 pb-4">
        <Button
          size="md"
          type="submit"
          variant="primary"
          className="w-full"
          disabled={isPending}
        >
          <span className="flex items-center justify-center">
            {isPending ? (
              <LoadingDots className="bg-white" />
            ) : (
              <>{isEdit ? `${t("Update_Address")}` : `${t("Save")}`}</>
            )}
          </span>
        </Button>
      </div>
    </form>
  );
};

export default AddressForm;
